import { cloneObject } from 'src/helpers/objectHelper';
import { $axios } from 'boot/axios';

const initialState = () => ({
  chains: [],
});

const state = initialState();

const getters = {
  getEmailsByChainGuid: (state) => (guid) => state.chains.find((chain) => chain.guid === guid)?.emails || [],
};

const actions = {
  // Chains
  async fetchChains({ commit }) {
    const { data } = await $axios.get('email-chains');
    commit('setChains', data.data.elements);
  },
  async fetchChain({ commit }, guid) {
    const { data } = (await $axios.get(`email-chains/${guid}`)).data;
    const chain = {
      ...data,
      smtpGUID: data.smtp?.guid,
    };
    commit('updateChain', chain);
    return chain;
  },
  async removeChain({ commit }, guid) {
    await $axios.delete(`email-chains/${guid}`);
    commit('removeChain', guid);
  },
  async updateChain({ commit }, body) {
    const { data } = await $axios.put(`email-chains/${body.guid}`, body);
    commit('updateChain', {
      ...data.data,
      smtpGUID: data.data.smtp?.guid,
    });
  },
  async createChain({ commit }) {
    const { data } = await $axios.post('email-chains');
    const chain = {
      ...data.data,
      emails: [],
    };
    commit('addChain', chain);
    return chain;
  },

  // Emails
  async fetchEmails({ commit }, chainGuid) {
    const { data } = await $axios.get(`email-chains/${chainGuid}`);
    commit('addEmailListToChain', data.data);
  },
  async createEmail({ commit }, { emailChainGuid, ...rest }) {
    const { data } = await $axios.post(`email-chains/${emailChainGuid}/emails`, rest);
    commit('addEmailToChain', {
      emailChainGuid,
      email: data.data,
    });
  },
  async updateEmail({ commit }, { emailChainGuid, ...rest }) {
    const { data } = await $axios.put(`email-chains/${emailChainGuid}/emails/${rest.guid}`, rest);
    commit('updateEmail', {
      emailChainGuid,
      email: data.data,
    });
  },
  async publishEmail({ commit }, { emailChainGuid, emailGuid, published }) {
    const { data } = await $axios.put(`email-chains/${emailChainGuid}/emails/${emailGuid}/publish`, {
      published,
    });
    commit('updateEmail', {
      emailChainGuid,
      email: data.data,
    });
  },
  async removeEmail({ commit }, { emailChainGuid, emailGuid }) {
    await $axios.delete(`email-chains/${emailChainGuid}/emails/${emailGuid}`);
    commit('removeEmail', { emailChainGuid, emailGuid });
  },
};

const mutations = {
  setChains(state, chains) {
    state.chains = chains;
  },
  removeChain(state, guid) {
    state.chains = state.chains.filter((email) => email.guid !== guid);
  },
  updateChain(state, chain) {
    state.chains = state.chains.map((item) => (item.guid === chain.guid ? chain : item));
  },
  addChain(state, chain) {
    state.chains.push(chain);
  },

  removeEmail(state, { emailChainGuid, emailGuid }) {
    const chainIndex = state.chains.findIndex(({ guid }) => guid === emailChainGuid);
    if (chainIndex === -1) {
      return;
    }

    const chain = cloneObject(state.chains[chainIndex]);
    const emailIndex = chain.emails.findIndex(({ guid }) => guid === emailGuid);
    if (emailIndex === -1) {
      return;
    }

    chain.emails.splice(emailIndex, 1);
    chain.emailsCount -= 1;
    state.chains[chainIndex] = chain;
  },
  updateEmail(state, { emailChainGuid, email }) {
    const chainIndex = state.chains.findIndex(({ guid }) => guid === emailChainGuid);
    if (chainIndex === -1) {
      return;
    }

    const chain = cloneObject(state.chains[chainIndex]);
    const emailIndex = chain.emails.findIndex(({ guid }) => guid === email.guid);
    if (emailIndex === -1) {
      return;
    }

    chain.emails[emailIndex] = email;
    state.chains[chainIndex] = chain;
  },
  addEmailToChain(state, { emailChainGuid, email }) {
    const chainIndex = state.chains.findIndex(({ guid }) => guid === emailChainGuid);
    if (chainIndex === -1) {
      return;
    }
    const chain = cloneObject(state.chains[chainIndex]);

    const emails = [
      ...chain.emails,
      email,
    ].sort((a, b) => {
      if (a.delay < b.delay) {
        return -1;
      }
      if (a.delay > b.delay) {
        return 1;
      }

      return 0;
    });

    state.chains[chainIndex] = {
      ...chain,
      emailsCount: chain.emailsCount + 1,
      emails,
    };
  },
  addEmailListToChain(state, { guid, emails, smtp }) {
    state.chains = state.chains.map((chain) => {
      if (chain.guid === guid) {
        return {
          ...chain,
          emails,
          smtpGUID: smtp?.guid,
        };
      }
      return chain;
    });
  },
};

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
};
